import { Fragment, useEffect, useState } from 'react';
import { Badge, Text } from '@components/base/display';
import { HStack, VStack } from '@components/base/layout';
import { taskService, type TaskTitleData } from '../api/taskService';
import {
  TASK_TITLE_CONFIG,
  formatScenarioText,
  splitTitleSegments,
  splitTitre1,
  splitTitre2,
  type TaskTitleConfig,
} from './taskTitleFormat';

export interface TaskTitleProps {
  /** Task (event) id — `id_event`. Absent on a task-less screen. */
  taskId?: string;
  /** Claim id — `id_sin`. */
  idSin?: string;
  /** Legacy single-line title, shown until the two-line title is available. */
  fallback: string;
  /** Labels / aria labels / separators. Defaults to {@link TASK_TITLE_CONFIG}. */
  config?: TaskTitleConfig;
}

interface TitleResult {
  /** The `idSin:taskId` pair the result belongs to (sequence guard). */
  requestKey: string;
  title: TaskTitleData | null;
}

function toRequestKey(idSin: string | undefined, taskId: string | undefined): string | null {
  return idSin && taskId ? `${idSin}:${taskId}` : null;
}

function MutedSeparator({ symbol }: Readonly<{ symbol: string }>) {
  return (
    <Text as="span" weight="normal" color="muted">
      {symbol}
    </Text>
  );
}

/**
 * Task detail page title (mahhp 22852).
 *
 * Fetches `TITRE1` / `TITRE2` (`boby-ws2-gs-sel-titre-tache`) for the claim +
 * task and renders them on two lines; until the title is known — loading,
 * error, no row, or no task at all (task-less screen) — it renders the legacy
 * single-line `fallback` h1 exactly as the page did before, so nothing shifts.
 *
 * Page chrome like the h1 it replaces: NOT a registered configurable
 * component. It stays configurable-ready — every label / aria label /
 * separator comes from a declarative {@link TaskTitleConfig} with stable keys
 * (`line1`, `line2`, `scenario`).
 */
export function TaskTitle({
  taskId,
  idSin,
  fallback,
  config = TASK_TITLE_CONFIG,
}: Readonly<TaskTitleProps>) {
  const [result, setResult] = useState<TitleResult | null>(null);
  const requestKey = toRequestKey(idSin, taskId);

  useEffect(() => {
    if (!idSin || !taskId) return;

    // Per-request guard: a response that lands after the ids changed (or
    // after unmount) is dropped; the derived `title` below additionally
    // ignores any result whose key no longer matches the current ids.
    const key = `${idSin}:${taskId}`;
    let current = true;
    taskService.fetchTaskTitle(idSin, taskId).then(
      (title) => {
        if (current) setResult({ requestKey: key, title });
      },
      () => {
        if (current) setResult({ requestKey: key, title: null });
      },
    );
    return () => {
      current = false;
    };
  }, [idSin, taskId]);

  const title = requestKey !== null && result?.requestKey === requestKey ? result.title : null;

  if (!title || title.titre1.trim() === '') {
    return (
      <Text as="h1" size="2xl" weight="bold" className="mb-6">
        {fallback}
      </Text>
    );
  }

  const { ref, rest } = splitTitre1(title.titre1);
  const segments = splitTitleSegments(rest);
  const { text, scenario } = splitTitre2(title.titre2);
  const hasLine2 = text !== '' || scenario !== null;

  return (
    <VStack
      gap={1}
      className="mb-6"
      role="group"
      aria-label={config.ariaLabel}
      data-testid="task-title"
    >
      <Text
        as="h1"
        size="2xl"
        weight="semibold"
        data-testid={`task-title-${config.line1.key}`}
      >
        {ref !== null && (
          <Text as="span" weight="bold" className="font-mono">
            {ref}
          </Text>
        )}
        {ref !== null && segments.length > 0 && (
          <>
            {' '}
            <MutedSeparator symbol={config.line1.refSeparator} />{' '}
          </>
        )}
        {segments.map((segment, index) => (
          <Fragment key={`${String(index)}-${segment}`}>
            {index > 0 && (
              <>
                {' '}
                <MutedSeparator symbol={config.line1.segmentSeparator} />{' '}
              </>
            )}
            {segment}
          </Fragment>
        ))}
      </Text>
      {hasLine2 && (
        <HStack gap={2} align="center" wrap data-testid={`task-title-${config.line2.key}`}>
          {text !== '' && (
            <Text as="span" size="base" color="muted" className="italic">
              {text}
            </Text>
          )}
          {text !== '' && scenario !== null && <MutedSeparator symbol={config.line2.separator} />}
          {scenario !== null && (
            <Badge
              size="sm"
              pill
              className="!normal-case !tracking-normal"
              aria-label={formatScenarioText(config.scenario.ariaLabel, scenario)}
              data-testid={`task-title-${config.scenario.key}`}
            >
              {formatScenarioText(config.scenario.label, scenario)}
            </Badge>
          )}
        </HStack>
      )}
    </VStack>
  );
}
